import type { ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { Chip } from '@hana/ui';
import { MS } from './data';
import s from './bod.module.css';

// Khối dùng chung cho các tab Lys (Mint-BOD): ô chỉ số · biểu đồ 12 tháng · thanh · mốc cảnh báo · danh sách.
export type Tone = 'ok' | 'warn' | 'err' | 'pri';
export type Span = 12 | 8 | 7 | 6 | 5 | 4;
const SPAN: Record<Span, string> = { 12: s.span12!, 8: s.span8!, 7: s.span7!, 6: s.span6!, 5: s.span5!, 4: s.span4! };
const TONE: Record<Tone, string> = { ok: s.cOk!, warn: s.cWarn!, err: s.cErr!, pri: s.cPri! };
const TXT: Record<Tone, string> = { ok: s.up!, warn: s.warnTx!, err: s.down!, pri: '' };

export type Kpi = { k: string; v: string; vtone?: Tone; u?: string; sub?: string; tone?: Tone; sp?: readonly number[] };

const spark = (a: readonly number[]): string => {
  const mn = Math.min(...a), r = Math.max(...a) - mn || 1;
  return a.map((v, i) => `${i ? 'L' : 'M'}${(2 + (i * 116) / (a.length - 1)).toFixed(1)} ${(21 - ((v - mn) / r) * 18).toFixed(1)}`).join(' ');
};

export function Kpis({ items }: { items: readonly Kpi[] }) {
  const t = useT();
  return (
    <section aria-label={t('bod.sec.metrics')} className={`${s.span12} ${s.tiles}`} style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map((x) => (
        <div key={x.k} className={s.tile}>
          <span className={s.tk}>{x.k}</span>
          <b className={`${s.tv} ${x.vtone ? TXT[x.vtone] : ''}`}>{x.v}{x.u && <small className={s.tu}> {x.u}</small>}</b>
          <span className={`${s.ts} ${x.tone ? TXT[x.tone] : ''}`}>{x.sub ?? ''}</span>
          {x.sp && <svg role="img" aria-label={t('bod.spark', { k: x.k, a: MS[0]!, b: MS[11]! })} width="100%" height="24" viewBox="0 0 120 24" preserveAspectRatio="none"><path d={`${spark(x.sp)} L118 24 L2 24 Z`} className={s.spA} /><path d={spark(x.sp)} className={s.spL} vectorEffect="non-scaling-stroke" /></svg>}
        </div>
      ))}
    </section>
  );
}

export function Card({ title, sub, span, acts, children }: { title: string; sub?: string; span: Span; acts?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={title} className={`${s.card} ${SPAN[span]}`}>
      <div className={s.ph}><h2>{title}</h2><span>{sub ?? ''}</span>{acts}</div>
      {children}
    </section>
  );
}

const CW = 720, CH = 180, SW = 60;
/** Biểu đồ cột 12 tháng (+ đường mốc). `fmt` định dạng giá trị cho mô tả. */
export function Chart12({ title, sub, span, ser, label, line, lineLabel, max, ymax, fmt, idx, lineTone = 'err' }: { title: string; sub: string; span: Span; ser: readonly number[]; label: string; line?: number; lineLabel?: string; max?: number; ymax: string; fmt?: (v: number) => string; idx: readonly number[]; lineTone?: Tone }) {
  const mx = max ?? Math.max(...ser, line ?? 0) * 1.12;
  const f = fmt ?? String;
  const y = (v: number) => (CH - (Math.max(0, v) / mx) * (CH - 12)).toFixed(1);
  const bars = ser.map((v, i) => { const h = (Math.max(0, v) / mx) * (CH - 12); return h > 0.2 ? `M${(i * SW + 12).toFixed(1)} ${y(v)}h36v${h.toFixed(1)}h-36z ` : ''; }).join('');
  return (
    <Card title={title} sub={sub} span={span}>
      <div className={s.leg}><span><i className={s.fTr} />{label}</span>{line != null && <span><i className={`${s.ln} ${TONE[lineTone]}`} />{lineLabel}</span>}<em>{ymax}</em></div>
      <svg role="img" aria-label={`${title}: ${MS.map((m, i) => `${m} ${f(ser[i]!)}`).join(', ')}`} width="100%" height="180" viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="none">
        <path d={idx.map((i) => `M${i * SW + 2} 0h56v${CH}h-56z`).join(' ')} className={s.hl} />
        <path d={[0.25, 0.5, 0.75, 1].map((q) => `M0 ${(CH - q * (CH - 12)).toFixed(1)}H${CW}`).join(' ') + ` M0 ${CH - 0.5}H${CW}`} className={s.gridL} vectorEffect="non-scaling-stroke" />
        <path d={bars} className={s.fTr} />
        {line != null && <path d={`M0 ${y(line)}H${CW}`} className={s.lineS} data-tone={lineTone} vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className={s.xl} aria-hidden="true">{MS.map((k, i) => <span key={k} className={idx.includes(i) ? s.xon : ''}>{k}</span>)}</div>
    </Card>
  );
}

export type BarRow = { k: string; n: number; tone?: Tone; v: string; s?: string; v2?: string };
export function BarsPanel({ title, sub, span, rows, vw, bold, cols }: { title: string; sub: string; span: Span; rows: readonly BarRow[]; vw?: string; bold?: boolean; cols?: 1 | 2 }) {
  const mx = Math.max(1, ...rows.map((r) => r.n));
  return (
    <Card title={title} sub={sub} span={span}>
      <div role="img" aria-label={`${title}: ${rows.map((r) => `${r.k} ${r.v}`).join(', ')}`} className={cols === 2 ? s.bars2 : s.bars1}>
        {rows.map((r, i) => (
          <div key={r.k} className={s.br} style={{ borderTop: i ? undefined : '0' }}>
            <div className={s.bl2}><span style={bold ? { fontWeight: 'var(--fw-label)' } : undefined}>{r.k}</span>{r.s && <small>{r.s}</small>}</div>
            <div className={s.bt}><div className={TONE[r.tone ?? 'pri']} style={{ width: `${Math.max(2, (r.n / mx) * 100).toFixed(1)}%` }} /></div>
            <b className={s.bv} style={vw ? { width: vw, whiteSpace: 'nowrap' } : undefined}>{r.v}{r.v2 && <small> {r.v2}</small>}</b>
          </div>
        ))}
      </div>
    </Card>
  );
}

export type Attn = { k: string; app: string; v: string; sev: Tone; s: string };
export function AttnPanel({ title, sub, span, rows }: { title: string; sub: string; span: Span; rows: readonly Attn[] }) {
  return (
    <Card title={title} sub={sub} span={span}>
      <div className={s.list}>
        {rows.map((r, i) => (
          <div key={r.k} style={{ borderTop: i ? undefined : '0' }}>
            <i className={`${s.dot} ${TONE[r.sev]}`} aria-hidden="true" />
            <span className={s.attnK}>{r.k}<small>{r.s} · {r.app}</small></span>
            <b className={TXT[r.sev]}>{r.v}</b>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ListPanel({ title, sub, span, rows }: { title: string; sub?: string; span: Span; rows: readonly (readonly [string, string, Tone?])[] }) {
  return (
    <Card title={title} sub={sub} span={span}>
      <div className={s.list}>
        {rows.map(([k, v, tone], i) => <div key={k} style={{ borderTop: i ? undefined : '0' }}><span>{k}</span><b className={tone ? TXT[tone] : ''}>{v}</b></div>)}
      </div>
    </Card>
  );
}

export const PctChip = ({ v, kind }: { v: string; kind: 'ok' | 'warn' }) => <Chip tone={kind}>{v}</Chip>;
